/**
 * Email normalization and typo suggestion.
 *
 * Auth is magic-link only, so the email address is the credential. A typo locks the
 * customer out permanently: they cannot sign in, and the change-email flow sits behind
 * sign-in. Syntax and MX checks do not catch the common cases, because typosquats like
 * `21gmail.com` are well-formed and accept mail.
 *
 * `suggestEmailCorrection` is advisory. Never block a submission on it: the domain list
 * cannot know a legitimate small domain from a typo of a big one.
 *
 * `getEmailError` is the opposite end: it only reports addresses that cannot exist as typed,
 * either because the shape is wrong (`a@b`, `a@gmail..com`, `a@gmail.com.`) or because the TLD
 * is one of the unambiguous misspellings below, none of which is a real TLD. Those are safe to
 * block on, because no amount of goodwill makes `.con` deliverable.
 */

/** Ordered by observed frequency in our own user table, then general popularity. */
const KNOWN_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'icloud.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'aol.com',
  'me.com',
  'ymail.com',
  'comcast.net',
  'verizon.net',
  'protonmail.com',
  'proton.me',
  'msn.com',
  'mail.com',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'cox.net',
  'charter.net',
  'earthlink.net',
  'optonline.net',
  'roadrunner.com',
  'rocketmail.com',
  'yahoo.co.uk',
  'googlemail.com',
  'outlook.co.uk',
  'hotmail.co.uk',
];

/** Misspelled TLDs that are unambiguous enough to suggest a fix for any domain. */
const TLD_TYPOS: Record<string, string> = {
  con: 'com',
  cim: 'com',
  cpm: 'com',
  clm: 'com',
  xom: 'com',
  vom: 'com',
  comm: 'com',
  ocm: 'com',
  cmo: 'com',
  cok: 'com',
  som: 'com',
  dom: 'com',
  nte: 'net',
  ner: 'net',
  nrt: 'net',
  orh: 'org',
  ogr: 'org',
};

/**
 * Trim, lowercase, and strip whitespace. Quoted local parts may legally contain spaces,
 * but no real signup does, and we have accounts stored as `x@gmail. com`.
 */
export function normalizeEmail(raw: string): string {
  return raw.replace(/\s+/g, '').toLowerCase();
}

/**
 * Stricter normalization for the sign-in boundary, where the value is a complete address
 * rather than something being typed. Keeps the protections in Auth.js's own default
 * normalizer (first two `@` parts, comma stripped from the domain) and adds whitespace
 * removal. Not safe for per-keystroke use, because partial input has no domain yet.
 */
export function normalizeEmailIdentifier(raw: string): string {
  if (!raw) throw new Error('Missing email from request body.');

  const [local, domain] = normalizeEmail(raw).split('@');

  if (!domain) return local;

  return `${local}@${domain.split(',')[0]}`;
}

/** Structural cleanup: duplicated or dangling dots that are never valid. */
function cleanDomain(domain: string): string {
  return domain
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+/, '')
    .replace(/\.+$/, '');
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    const current = [i];

    for (let j = 1; j <= b.length; j++) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);

      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, substitution);
    }

    previous = current;
  }

  return previous[b.length];
}

function splitEmail(email: string): { local: string; domain: string } | null {
  const at = email.lastIndexOf('@');

  if (at <= 0 || at === email.length - 1) return null;

  return { local: email.slice(0, at), domain: email.slice(at + 1) };
}

/**
 * Suggest a corrected address, or null when the address looks fine or is too far from
 * anything we recognize. The caller shows this as "did you mean", never as an error.
 */
export function suggestEmailCorrection(rawEmail: string): string | null {
  const email = normalizeEmail(rawEmail);
  const parts = splitEmail(email);

  if (!parts) return null;

  const { local, domain } = parts;
  const suggested = suggestDomainCorrection(domain);

  return suggested ? `${local}@${suggested}` : null;
}

/**
 * A user-facing reason the address cannot be delivered to, or null when it is worth accepting.
 * Deliberately shape-only: anything that could be a real address passes, and the "did you mean"
 * hint handles the rest.
 */
export function getEmailError(rawEmail: string): string | null {
  const email = normalizeEmail(rawEmail);

  if (!email) return null;

  const parts = email.split('@');

  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return 'Enter an email address like name@example.com';
  }

  const [local, domain] = parts;

  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
    return 'Check the dots: name@example.com';
  }

  if (!domain.includes('.') || domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
    return 'Check the part after the @: name@example.com';
  }

  if (/[^a-z0-9.!#$%&'*+/=?^_`{|}~-]/.test(local) || /[^a-z0-9.-]/.test(domain)) {
    return 'Remove any unusual characters from the address';
  }

  const tld = domain.slice(domain.lastIndexOf('.') + 1);

  // States the problem rather than the fix, because the "did you mean" hint sits directly
  // below with the whole corrected address and a button to accept it.
  if (TLD_TYPOS[tld]) {
    return `Check the ending, for example .${TLD_TYPOS[tld]}`;
  }

  if (tld.length < 2 || /[^a-z]/.test(tld)) {
    return 'Check the ending, for example .com';
  }

  return null;
}

/** Exported for the admin triage script, which works on domains directly. */
export function suggestDomainCorrection(rawDomain: string): string | null {
  const domain = cleanDomain(rawDomain);

  if (!domain.includes('.')) return null;

  // A known domain needs no suggestion, unless structural cleanup already changed it
  // (`yahoo..com`, `gmail.com.`), in which case the cleaned form is the suggestion.
  if (KNOWN_DOMAINS.includes(domain)) return domain === rawDomain ? null : domain;

  // Text run onto the end of a real domain, e.g. `gmail.com3039092221`. A remainder that
  // starts with a dot is usually a real country domain (`yahoo.com.br`), so only treat it
  // as a typo when it repeats the TLD we just matched (`gmail.com.com`).
  const runOn = KNOWN_DOMAINS.find((known) => {
    if (!domain.startsWith(known) || domain.length === known.length) return false;

    const remainder = domain.slice(known.length);

    return !remainder.startsWith('.') || remainder === known.slice(known.lastIndexOf('.'));
  });

  if (runOn) return runOn;

  const labels = domain.split('.');
  const tld = labels[labels.length - 1];
  const tldFix = TLD_TYPOS[tld];

  if (tldFix) return [...labels.slice(0, -1), tldFix].join('.');

  let best: { domain: string; distance: number } | null = null;

  for (const known of KNOWN_DOMAINS) {
    // Scale tolerance to the longer string so truncations like `gmail.c` still match.
    const maxDistance = Math.max(domain.length, known.length) <= 8 ? 1 : 2;
    const distance = levenshtein(domain, known);

    if (distance <= maxDistance && (!best || distance < best.distance)) {
      best = { domain: known, distance };
    }
  }

  if (best) return best.domain;

  return domain === rawDomain ? null : domain;
}
